import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { signInForKakao, KAKAO_AUTH_URL } from "#/auth/signIn";
import { Splash } from "@/Splash";
import { useEffect, useState, useCallback } from "react";
import { useCookies } from "react-cookie";
import { useStorage } from "^/common/useStorage";
import { AppType, SignType } from "#/unique/domain";
import {
  businessAppLogin,
  useBusinessLogin,
  userAppLogin,
  useUserLogin,
} from "~/services/auth";
import { SHA256 } from "crypto-js";
import { OAuthErrorView } from "@/common/OAuthErrorView";
import { clearForceRelogin } from "#/auth/signIn";

export const Route = createFileRoute("/oauth/kakao")({
  component: RouteComponent,
});

function RouteComponent() {
  const [, setCookie] = useCookies(["email"]);
  const [type] = useStorage<AppType>("session-app-type");
  const [, setSns] = useStorage<SignType>("local-sign-sns-type");
  const [, setFcm] = useStorage<string>("session-fcm");
  const [, setSnsId] = useStorage<string>("local-sns-id");
  const [shouldCheckBusinessSign, setShouldCheckBusinessSign] = useState(false);
  const [shouldCheckCustomerSign, setShouldCheckCustomerSign] = useState(false);
  // const hasCheckedSign = useRef(false);
  const params = new URL(document.URL).searchParams;
  const code = params.get("code");
  const errorParam = params.get("error");

  // 마운트 시 1회만 — 매 렌더마다 찍히지 않도록
  useEffect(() => {
    console.log("[OAUTH-KAKAO] 콜백 진입", {
      url: document.URL,
      code: code ? `${code.substring(0, 20)}...` : "❌ NO CODE",
      error: errorParam || null,
      allParams: Object.fromEntries(params.entries()),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, error: queryError } = useQuery({
    queryKey: ["/oauth/kakao"],
    queryFn: () => signInForKakao({ code }),
    enabled: !!code && !errorParam,
    retry: false,
    select: (res) => res.data,
  });

  // 응답 도착 시점에만 1회씩
  useEffect(() => {
    if (queryError) console.error("[OAUTH-KAKAO] signInForKakao 실패", queryError);
  }, [queryError]);
  useEffect(() => {
    if (data) console.log("[OAUTH-KAKAO] 사용자 정보 받음", { id: data.id });
  }, [data]);

  const { history } = useRouter();

  // useBusinessLogin을 컴포넌트 레벨에서 호출
  const {
    data: businessLoginData,
    error: businessError,
    isSuccess: isBusinessSuccess,
  } = useBusinessLogin(
    {
      snsId: data?.id || "",
      cdSnsType: "SNS_TYPE_KAKAO",
    },
    shouldCheckBusinessSign
  );

  // useUserLogin을 컴포넌트 레벨에서 호출
  const {
    data: userLoginData,
    error: userError,
    isSuccess: isUserSuccess,
  } = useUserLogin(
    {
      snsId: data?.id || "",
      cdSnsType: "SNS_TYPE_KAKAO",
    },
    shouldCheckCustomerSign
  );

  useEffect(() => {
    if (!data) return;

    // 카카오 인증 성공 — 강제 재인증 플래그 제거 (다음부터는 자연스러운 SSO 허용)
    clearForceRelogin();

    // 앱 환경 감지 (MT_I 또는 MT_A가 포함된 UserAgent)
    const userAgent = navigator.userAgent;
    const isApp = /MT_I|MT_A/i.test(userAgent);
    const key = "SNS_TYPE_KAKAO" + data.id;
    const hash = SHA256(key).toString();
    // 앱 환경에서만 appLogin 호출
    if (/MT_A/i.test(userAgent)) {
      window.mung.appLogin(hash);
    }
    if (/MT_I/i.test(userAgent)) {
      const array = ["appLogin", hash];
      window.webkit?.messageHandlers?.mung?.postMessage(array);
    }
    const fnAppLogin = async (fcm: string) => {
      try {
        const appLoginData = {
          cdSnsType: "SNS_TYPE_KAKAO",
          hash: hash,
          fcmToken: fcm,
          cdMobileType: /MT_A/i.test(userAgent) ? "ANDROID" : "IOS",
        };
        if (import.meta.env.VITE_APP_TYPE === "business") {
          const loginResponse = await businessAppLogin(appLoginData);
          console.log("앱 로그인 성공:", loginResponse);
          if (loginResponse?.statusCode === 200) {
            history.push("/business/layout/home");
          } else {
            setShouldCheckBusinessSign(type === "business");
            console.log("앱 로그인 실패:", loginResponse?.statusCode);
          }
        } else {
          const loginResponse = await userAppLogin(appLoginData);
          if (loginResponse?.statusCode === 200) {
            history.push("/customer/layout/home");
          } else {
            setShouldCheckCustomerSign(type === "customer");
            console.log("앱 로그인 실패:", loginResponse?.statusCode);
          }
        }
      } catch (error) {
        console.log("앱 로그인 실패:", error);
        console.error("앱 로그인 실패:", error);
      }
    };

    if (isApp) {
      window.callAppLogin = (data: string) => {
        setFcm(data);
        fnAppLogin(data);
      };
    }

    // 웹 환경에서는 바로 로그인 진행
    if (!isApp) {
      setShouldCheckBusinessSign(type === "business");
      setShouldCheckCustomerSign(type === "customer");
    }

    setSnsId(data.id);
    setSns("SNS_TYPE_KAKAO");
    setCookie("email", data.kakao_account.email, {
      secure: true,
      maxAge: 3600,
    });
  }, [data, setCookie, setSns, type]);

  useEffect(() => {
    if (type === "business" && isBusinessSuccess) {
      businessLogin();
    } else if (type === "customer" && isUserSuccess) {
      customerLogin();
    }
  }, [isBusinessSuccess, isUserSuccess, type]);

  const businessLogin = useCallback(() => {
    if (isBusinessSuccess && businessLoginData?.statusCode == 201) {
      history.push("/sign-up");
    } else if (isBusinessSuccess && businessLoginData?.statusCode == 200) {
      history.push("/business/layout/home");
    } else {
      console.log("앱 로그인 실패:", businessError);
      history.push("/");
    }
  }, [isBusinessSuccess, businessLoginData, businessError, history]);

  const customerLogin = useCallback(() => {
    if (isUserSuccess && userLoginData?.statusCode == 201) {
      history.push("/sign-up");
    } else if (isUserSuccess && userLoginData?.statusCode == 200) {
      history.push("/customer/layout/home");
    } else {
      history.push("/");
    }
  }, [isUserSuccess, userLoginData, userError, history]);

  // ── 에러 분기
  if (errorParam) {
    return <OAuthErrorView error={errorParam} provider="kakao" retryUrl={KAKAO_AUTH_URL} />;
  }
  if (!code) {
    return <OAuthErrorView error="no_code" provider="kakao" retryUrl={KAKAO_AUTH_URL} />;
  }
  if (queryError) {
    return <OAuthErrorView error="token_exchange_failed" provider="kakao" retryUrl={KAKAO_AUTH_URL} />;
  }

  return <Splash visible />;
}
